import { Button, Stack, Box, Checkbox } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { tease } from "../components/CheckButton";
import { GoogleLogin, useGoogleLogin } from "@react-oauth/google";
import {
  AccountType,
  ActionType,
  Color,
  ContactType,
  defaultInnerStyle,
  ErrorCode,
  FontStyle,
  navPaddingTop,
  SignupFor,
  Slug,
  SurveyButtonStyle,
} from "../constants";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { StyledTextField } from "../components/AutocompleteUtils";
import * as apiService from "../service/ApiService";
import { useDispatch, useSelector } from "react-redux";
import googleIcon from "../img/googleIcon.png";
import { useNavigate } from "react-router-dom";
import { clearError } from "../utils/actionUtils";

// signup redirect from Fetti University

const styles = {
  outer: {
    paddingTop: navPaddingTop,
    backgroundColor: "#fff",
    minHeight: "100vh",
  },
  container: {
    paddingTop: "1.5rem",
    height: "100%",
    px: 3,
    maxWidth: "100vw",
  },
  inner: {
    ...defaultInnerStyle,
    margin: "auto",
    paddingBottom: "50px",
    "& .MuiFormControl-root": {
      border: "none",
    },
  },

  subtitle: {
    ...FontStyle.body,
    fontSize: { xs: "14px", sm: "16px" },
    textAlign: "center",
  },
  title: {
    ...FontStyle.body,
    fontSize: { xs: "32px", sm: "48px" },
    textAlign: "center",
  },
  body: {
    ...FontStyle.body,
    fontSize: "0.83rem",
    textAlign: "left",
    lineHeight: "1.4",
    maxWidth: "500px",
    "& > * >a": {
      color: "#000",
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  label: {
    ...FontStyle.headingBody,
    fontSize: { xs: "14px", sm: "1rem" },
    // lineHeight: "1.4",
    width: "100%",
    maxWidth: "500px",
  },
  search: {
    width: "100%",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "4px",
    border: "1px solid rgb(204, 204, 204)",
  },
  searchProps: {
    disableUnderline: true,
  },
  error: {
    ...FontStyle.body,
    color: "red",
    fontSize: "0.75rem",
    textAlign: "left",
    width: "100%",
  },
  success: {
    ...FontStyle.body,
    color: "green",
    fontSize: "16px",
    textAlign: "center",
    width: "100%",
  },
  googleCustomButton: {
    textTransform: "none",
    fontFamily: '"Google Sans",arial,sans-serif',
    fontWeight: "500",
    height: "46px",
    width: "100%",
    border: "1px solid #dadce0",
    color: "#202124",
    "&:hover": {
      border: "1px solid #d2e3fc",
    },
  },
  signupWithEmailText: {
    ...FontStyle.headingBody,
    fontSize: { xs: "16px", sm: "1.2rem" },
    lineHeight: "1.4",
    width: "100%",
    maxWidth: "500px",
    textAlign: "center",
    paddingTop: 1,
  },
  orText: {
    ...FontStyle.body,
    fontSize: "1rem",
    // px: '1rem',
    textAlign: "center",
    opacity: "0.4",
  },
  bar: {
    width: "100%",
    height: "1px",
    backgroundColor: "#000",
    opacity: "0.2",
  },
};
const EduSignup = () => {
  const queryParameters = new URLSearchParams(window.location.search);
  const signupFor = queryParameters.get("for");
  const forAffiliate = signupFor === SignupFor.affiliate;
  const accountType = forAffiliate
    ? AccountType.affiliate
    : AccountType.standard;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [receiveEmails, setReceiveEmails] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorForGoogle, setErrorForGoogle] = useState(false);
  const googleCallback = (response) => {
    console.log("got google response", response);
    const { access_token } = response;
    if (access_token) {
      setErrorForGoogle(true);
      clearError(dispatch);
      apiService.register(
        dispatch,
        {
          password: access_token,
          contactType: ContactType.google,
          accountType,
        },
        handleSuccess
      );
    }
  };
  // http://localhost:3003/sign-up/?post=1050
  const handleSuccess = () => {
    navigate({
      pathname: Slug.signUpSuccess + "/",
      search: "?" + queryParameters.toString(),
    });
  };
  const customGoogleLogin = useGoogleLogin({
    onSuccess: googleCallback,
    onError: (e) => console.log("Login Failed", e),
  });

  const handleSubmit = (e) => {
    let foundError = false;
    if (firstName.length < 2) {
      setErrors((e) => {
        return { ...e, firstName: "First name must be at least 2 characters" };
      });
      foundError = true;
    } else {
      setErrors((e) => {
        return { ...e, firstName: "" };
      });
    }
    if (lastName.length < 2) {
      setErrors((e) => {
        return { ...e, lastName: "Last name must be at least 2 characters" };
      });
      foundError = true;
    } else {
      setErrors((e) => {
        return { ...e, lastName: "" };
      });
    }
    if (!email.includes("@")) {
      setErrors((e) => {
        return { ...e, email: "Email must be valid" };
      });
      foundError = true;
    } else {
      setErrors((e) => {
        return { ...e, email: "" };
      });
    }
    if (password.length < 8) {
      setErrors((e) => {
        return { ...e, password: "Password must be at least 8 characters" };
      });
      foundError = true;
    } else {
      setErrors((e) => {
        return { ...e, password: "" };
      });
    }
    if (!foundError) {
      setErrorForGoogle(false);
      clearError(dispatch);
      apiService.register(
        dispatch,
        {
          firstName,
          lastName,
          contactInfo: email,
          password,
          contactType: ContactType.email,
          accountType,
        },
        handleSuccess
      );
    }
  };

  return (
    <Box sx={styles.outer}>
      <Box sx={styles.container}>
        <Stack
          direction="column"
          sx={styles.inner}
          alignItems="left"
          justifyContent="center"
          spacing={1}>
          <Box sx={styles.title}>
            Join{" "}
            <span
              style={{
                ...FontStyle.logo,
                position: "relative",
                backgroundColor: Color.primary,
              }}>
              {forAffiliate ? "Fetti Affiliate." : "Fetti University."}
            </span>
          </Box>
          <Box sx={styles.subtitle}>
            {forAffiliate
              ? "Become an affiliate now!"
              : "Get access to hundreds of proven career resources, created by real recruiters from top companies."}
          </Box>
          <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Button sx={styles.googleCustomButton} onClick={customGoogleLogin}>
              <img
                src={googleIcon}
                style={{ width: "18px", height: "18px", marginRight: "8px" }}
              />
              Sign up with Google
            </Button>
          </Box>
          {errorForGoogle && auth.error === ErrorCode.emailAlreadyExists && (
            <Box sx={styles.error}>
              An account with this email already exists!
            </Box>
          )}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={2}>
            <Box sx={styles.bar} />
            <Box sx={styles.orText}>or</Box>
            <Box sx={styles.bar} />
          </Stack>

          <Box sx={styles.signupWithEmailText}>
            Sign up with your email address
          </Box>
          <Box sx={styles.label}>First Name</Box>
          <StyledTextField
            InputProps={styles.searchProps}
            sx={styles.search}
            variant="standard"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          {errors.firstName && <Box sx={styles.error}>{errors.firstName}</Box>}
          <Box sx={styles.label}>Last Name</Box>
          <StyledTextField
            InputProps={styles.searchProps}
            sx={styles.search}
            variant="standard"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          {errors.lastName && <Box sx={styles.error}>{errors.lastName}</Box>}
          <Box sx={styles.label}>Email</Box>
          <StyledTextField
            InputProps={styles.searchProps}
            sx={styles.search}
            variant="standard"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && <Box sx={styles.error}>{errors.email}</Box>}
          {!errorForGoogle && auth.error === ErrorCode.emailAlreadyExists && (
            <Box sx={styles.error}>
              An account with this email already exists!
            </Box>
          )}
          <Box sx={styles.label}>Password</Box>
          <StyledTextField
            InputProps={styles.searchProps}
            sx={styles.search}
            variant="standard"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {errors.password && <Box sx={styles.error}>{errors.password}</Box>}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-start"
            alignItems="center"
            sx={{ paddingTop: 1, cursor: "pointer" }}
            onClick={() => setReceiveEmails(!receiveEmails)}>
            <Checkbox
              checked={receiveEmails}
              sx={{ padding: 0 }}
              size="large"
              style={{
                color: !receiveEmails ? "rgb(119, 119, 119)" : "black",
              }}
            />
            <Box sx={{ ...styles.body, textAlign: "left" }}>
              Yes, I would like to receive career opportunities and other
              information via email.
            </Box>
          </Stack>
          <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Stack
              direction="column"
              sx={{ ...styles.body, textAlign: "left", paddingLeft: "5px" }}
              spacing={2}>
              <Box>
                By signing up you agree to our{" "}
                <a
                  href={Slug.privacyPolicy}
                  style={{ fontWeight: "bold" }}
                  target="_blank">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href={Slug.termsAndConditions}
                  style={{ fontWeight: "bold" }}
                  target="_blank">
                  Terms & Conditions
                </a>
                .
              </Box>
            </Stack>
          </Box>
          <Button
            sx={{
              ...SurveyButtonStyle.defaultStyling,
              ...SurveyButtonStyle.primaryColor,
              // ...SurveyButtonStyle.gradientColor,
              // textTransform: "none",
              // fontWeight: "normal",
            }}
            disableRipple
            fullWidth
            onClick={() => {
              handleSubmit();
            }}>
            SIGN UP
          </Button>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", paddingTop: "24px" }}>
            <Box sx={{ ...styles.bar, width: "100%" }} />
          </Stack>
          <Stack
            sx={{ ...styles.panel, paddingTop: 2 }}
            direction="column"
            alignItems="center"
            spacing="1rem">
            <Box sx={{ ...styles.body, fontSize: "16px", fontWeight: "bold" }}>
              Already have an account?
            </Box>
            <Button
              sx={{
                ...SurveyButtonStyle.defaultStyling,
                ...SurveyButtonStyle.yellowColor,
              }}
              disableRipple
              fullWidth
              onClick={() =>
                navigate({
                  pathname: Slug.login + "/",
                  search: "?" + queryParameters.toString(),
                })
              }>
              LOG IN
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default EduSignup;
